import React, { useRef, forwardRef, useState, useImperativeHandle } from "react";

const MAX_SAFE_DIGITS = 15;

function isDigit(char: string): boolean {
  return char >= "0" && char <= "9";
}

function sanitizeInput(raw: string, decimalDigits: number) {
  raw = decimalDigits ? raw.replace(/[^\d.]/g, "") : raw.replace(/[^\d]/g, "");
  if (!raw) return "";

  if (decimalDigits > 0) {
    // 1,234.56 -> 1234.56
    const match = raw.match(/^(\d*)(\.?)(\d*)/);

    // 1234.56 -> 1234
    let intPart = match ? match[1] : "";

    // 1234.56 -> .
    const dot = match ? match[2] : "";

    // 1234.56 -> 56
    let decPart = match ? match[3] : "";

    intPart = intPart.slice(0, MAX_SAFE_DIGITS);
    decPart = decPart.slice(0, decimalDigits);

    if (dot || decPart.length > 0) {
      return `${intPart}${dot}${decPart}`;
    }

    return intPart;
  }

  return raw.slice(0, MAX_SAFE_DIGITS);
}

function addThousandsSeparators(intPart: string): string {
  // Adds commas as thousands separators to the integer part of a number string
  // (finds groups of 3 digits and adds commas)
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatWithCommas(numStr: string): string {
  if (!numStr) return "";
  const [intPart, decPart] = numStr.split(".");
  const intFormatted = addThousandsSeparators(intPart);
  return decPart !== undefined ? `${intFormatted}.${decPart}` : intFormatted;
}

function formatDecimal(numStr: string, decimalDigits: number): string {
  if (!numStr) return "";
  const num = parseFloat(numStr);
  if (isNaN(num)) return "";
  return num.toFixed(decimalDigits);
}

function calcCursorPosition(raw: string, formatted: string, selectionStart: number) {
  let { digitsBeforeCursorInRaw, rawDot } = getCurrentPosInRaw(selectionStart, raw);

  // Walk formatted string to same count
  return calcPositionInFormatted(formatted, digitsBeforeCursorInRaw, rawDot);
}

function getCurrentPosInRaw(selectionStart: number, raw: string) {
  let digitsBeforeCursorInRaw = 0,
    rawDot = false;
  for (let i = 0; i < selectionStart; i++) {
    if (isDigit(raw[i])) digitsBeforeCursorInRaw++;
    if (!rawDot && raw[i] === ".") rawDot = true;
  }
  return { digitsBeforeCursorInRaw, rawDot };
}

function calcPositionInFormatted(formatted: string, digitsBeforeCursorInRaw: number, rawDot: boolean) {
  let digitsBeforeCursorInFormatted = 0,
    fDot = false,
    pos = 0;
  for (; pos < formatted.length; pos++) {
    if (isDigit(formatted[pos])) digitsBeforeCursorInFormatted++;
    if (!fDot && formatted[pos] === ".") fDot = true;
    if (digitsBeforeCursorInFormatted === digitsBeforeCursorInRaw && fDot === rawDot) {
      break;
    }
  }

  if (pos < formatted.length) pos++;
  return pos;
}
function handleBackspaceAfterComma(
  ref: React.RefObject<HTMLInputElement> | null,
  selectionStart: number,
  e: React.KeyboardEvent<HTMLInputElement>,
) {
  setCursor(ref, selectionStart - 1, e);
}

function handleDeleteBeforeComma(
  ref: React.RefObject<HTMLInputElement> | null,
  selectionStart: number,
  e: React.KeyboardEvent<HTMLInputElement>,
) {
  setCursor(ref, selectionStart + 1, e);
}

function setCursor(
  ref: React.RefObject<HTMLInputElement> | null,
  selectionStart: number,
  e: React.KeyboardEvent<HTMLInputElement> | null = null,
) {
  const actualInput = ref?.current;
  if (actualInput) {
    actualInput.setSelectionRange(selectionStart, selectionStart);
    setTimeout(() => {
      actualInput.setSelectionRange(selectionStart, selectionStart);
    }, 0);
  }
  e?.preventDefault();
}

function checkDeleteAtStart(rawValue: string, selectionStart: number, formattedValue: string) {
  let isDeleteAtStart = false;
  if (
    (rawValue.length > 0 && selectionStart === 0) ||
    (rawValue.length > 0 && selectionStart === 1 && rawValue[0] === ",")
  ) {
    if (formattedValue.startsWith(",")) formattedValue = formattedValue.slice(1);
    isDeleteAtStart = true;
  }
  return { isDeleteAtStart, formattedValue };
}

export interface USFormatProps {
  decimalDigits?: number;
}

function parseInput(sanitized: string) {
  let parsedValue: number | null = null;
  if (sanitized) {
    const num = parseFloat(sanitized);
    if (!isNaN(num)) {
      parsedValue = num;
    }
  }
  return parsedValue;
}

interface MustHaveProps {
  value?: string | number | boolean | object | [any] | null;
  onBlur?: (name: string) => void;
  onValueChanged?: (data: { value: any }) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  inputType?: string;
}

export function withUSFormattedNumberInput<TProps extends MustHaveProps>(
  WrappedComponent: React.ComponentType<TProps>,
) {
  return forwardRef<HTMLInputElement, TProps & USFormatProps>((props, ref) => {
    const { value, onValueChanged, decimalDigits = 0, onBlur } = props;
    const internalRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => internalRef?.current as HTMLInputElement, []);

    const [editingValue, setEditingValue] = useState<string | null>(null);
    const isEditing = editingValue !== null;

    // keeping cursor position intact after formatting applied
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.currentTarget;
      const selectionStart = input.selectionStart ?? 0;
      const selectionEnd = input.selectionEnd ?? 0;
      const val = input.value;
      if (selectionStart !== selectionEnd) return;
      if (e.key === "Delete" && val[selectionStart] === ",") {
        handleDeleteBeforeComma(internalRef, selectionStart, e);
      } else if (e.key === "Backspace" && val[selectionStart - 1] === ",") {
        handleBackspaceAfterComma(internalRef, selectionStart, e);
      }
    };

    const handleChange = (data: { value: string }) => {
      const rawValue = data.value;
      const selectionStart = internalRef.current?.selectionStart ?? 0;
      const sanitized = sanitizeInput(rawValue, decimalDigits);
      setEditingValue(sanitized);

      let formattedValue = formatWithCommas(sanitized);
      if (formattedValue.startsWith(",")) formattedValue = formattedValue.slice(1);

      // Special case: deleting first digit at start (Backspace/Delete)
      let isDeleteAtStart;
      ({ isDeleteAtStart, formattedValue } = checkDeleteAtStart(rawValue, selectionStart, formattedValue));

      let newPos = calcCursorPosition(rawValue, formattedValue, selectionStart);

      let parsedValue: number | null = parseInput(sanitized);

      if (onValueChanged) {
        onValueChanged({ value: parsedValue });
      }

      if (internalRef.current) {
        internalRef.current.value = formattedValue;
        if (isDeleteAtStart || newPos < 1) newPos = 0;
        setCursor(internalRef, newPos);
      }
    };

    const handleBlur = (propertyName: string) => {
      const input = internalRef.current;
      const rawValue = input ? input.value : "";
      const sanitized = sanitizeInput(rawValue, decimalDigits);
      let displayValue = "";
      if (sanitized) {
        const num = parseFloat(sanitized);
        if (!isNaN(num)) {
          const options = { minimumFractionDigits: decimalDigits, maximumFractionDigits: decimalDigits };
          displayValue = num.toLocaleString("en-US", decimalDigits ? options : undefined);
        }
      }

      setEditingValue(displayValue);

      let parsedValue: number | null = parseInput(sanitized);
      if (onValueChanged) {
        onValueChanged({ value: parsedValue });
      }

      if (onBlur) {
        onBlur(propertyName);
      }
    };

    let inputValue = "";
    if (isEditing) {
      inputValue = editingValue ? formatWithCommas(editingValue) : "";
    } else if ((typeof value === "number" && !isNaN(value)) || typeof value === "string") {
      inputValue = formatWithCommas(formatDecimal(value.toString(), decimalDigits));
    }

    return (
      <WrappedComponent
        ref={internalRef}
        {...props}
        value={inputValue}
        onValueChanged={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        inputType="text"
      />
    );
  });
}
